'use client'

import { useEffect, useRef, useState, MouseEvent } from 'react'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import Image from 'next/image'
import { UploadIcon, Trash2Icon } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ContentFileIcon, FileForm, FilesFilters, LoadingState, DeleteConfirmationDialog } from '@/components/partials'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { CreateFileParams, FilesFilterParams, FileType } from '@/types/file'
import { useCreateFile, useFiles, useDeleteFile, useBulkDeleteFiles } from '@/hooks'
import { formatFileSize, getResizedPictureUrl } from '@/lib/utils'
import { BulkDeleteResponse } from '@/types/general'
import { hasPermission } from '@/lib/permissions'
import { FormModeEnum, SortEnum } from '@/enums'
import { useMeStore } from '@/store'

export type PropsType = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (files: FileType[]) => void
  selectedFiles?: FileType[]
  multiple?: boolean
  onlyImages?: boolean
  optimizeImageSelection?: boolean
  types?: string[]
  title?: string
  description?: string
  closeOnSelect?: boolean
  maxSelectionLimit?: number
}

const FileManager = ({
  open,
  onOpenChange,
  onSelect,
  selectedFiles = [],
  multiple = false,
  onlyImages = false,
  optimizeImageSelection = false,
  types = [],
  title,
  description,
  closeOnSelect = false,
  maxSelectionLimit
}: PropsType) => {
  const t = useTranslations()
  const searchParams = useSearchParams()

  // Get current user for permission check
  const { meState } = useMeStore()
  const currentUser = meState.response

  // Check if a user has deleted permission
  const canDeleteFiles = hasPermission(currentUser, 'FILES', 'DELETE')

  // Local state for selected files
  const [localSelectedFiles, setLocalSelectedFiles] = useState<FileType[]>(selectedFiles)
  // State for showing/hiding the upload form
  const [showUploadForm, setShowUploadForm] = useState<boolean>(false)
  // State for upload loading (for UI state management)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  // State for delete confirmation
  const [fileToDelete, setFileToDelete] = useState<FileType | null>(null)
  // State for bulk delete confirmation
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  // Ref for the file form
  const fileFormRef = useRef<any>(null)

  // Reset local selection when the modal is opened or selectedFiles prop changes
  useEffect(() => {
    if (open) {
      setLocalSelectedFiles(selectedFiles)
      setShowUploadForm(false) // Hide the upload form when modal is opened
    }
  }, [open, selectedFiles])

  // Initialize filters from URL parameters
  const params = new URLSearchParams(searchParams.toString())
  const initialFilters: FilesFilterParams = {
    page: parseInt(params.get('page') || '1'),
    size: parseInt(params.get('size') || '20'),
    sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
    sortBy: params.get('sortBy') || 'createdAt',
    q: params.get('q') || '',
    onlyImages: onlyImages ? true : undefined,
    types: types.length > 0 ? types : undefined
  }

  // Local state for filters
  const [filters, setFilters] = useState<FilesFilterParams>(initialFilters)

  // Update filters when onlyImages or types prop changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      onlyImages: onlyImages ? true : undefined,
      types: types.length > 0 ? types : undefined
    }))
  }, [onlyImages, types])

  // File selection functions
  const toggleFileSelection = (file: FileType) => {
    let newSelection: FileType[]

    if (multiple) {
      // For multiple selection
      const isSelected = localSelectedFiles.some(f => f.id === file.id)
      if (isSelected) {
        // Remove from selection
        newSelection = localSelectedFiles.filter(f => f.id !== file.id)
      } else {
        // Check if we've reached the maximum selection limit
        if (maxSelectionLimit && localSelectedFiles.length >= maxSelectionLimit) {
          // Show a toast notification
          toast.error(t('max_selection_limit_reached', { limit: maxSelectionLimit }))
          return
        }
        // Add to selection
        newSelection = [...localSelectedFiles, file]
      }
      setLocalSelectedFiles(newSelection)

      // Immediately call onSelect with the new selection
      onSelect(newSelection)
    } else {
      // For a single selection, check if the file is already selected
      const isSelected = localSelectedFiles.length === 1 && localSelectedFiles[0].id === file.id

      if (isSelected) {
        // If already selected, clear the selection
        newSelection = []
      } else {
        // Otherwise, select the file
        newSelection = [file]
      }

      setLocalSelectedFiles(newSelection)

      // Immediately call onSelect with the new selection
      onSelect(newSelection)
    }

    // Only close the dialog if closeOnSelect is true
    if (closeOnSelect) {
      onOpenChange(false)
    }
  }

  const isFileSelected = (fileId: string) => {
    return localSelectedFiles.some(f => f.id === fileId)
  }

  // Fetch files
  const { data: filesData, isLoading, error, refetch } = useFiles(filters)

  // Handle filter changes
  const handleFiltersChange = (newFilters: Partial<FilesFilterParams>) => {
    // Update the filters state with the new filters
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      // Reset page to 1 if any filter other than the page changes
      page: newFilters.page || (Object.keys(newFilters).length > 0 && !newFilters.page ? 1 : prev.page)
    }))
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  // Use the createFile mutation
  const { mutateAsync: createFileMutation, uploadProgress: mutationProgress, isPending: isCreating } = useCreateFile()

  // Handle file upload
  const handleFileUpload = async (data: CreateFileParams) => {
    setIsUploading(true)

    try {
      // Use the actual file upload API
      const uploadedFile = await createFileMutation(data)

      // Update UI after successful upload
      setIsUploading(false)
      setShowUploadForm(false)
      toast.success(t('file_uploaded_successfully'))

      // Refresh the file list and select the newly uploaded file
      await refetch()

      // Select the newly uploaded file
      if (uploadedFile) {
        if (multiple) {
          // Check if we've reached the maximum selection limit
          if (maxSelectionLimit && localSelectedFiles.length >= maxSelectionLimit) {
            // Show a toast notification
            toast.warning(t('max_selection_limit_reached', { limit: maxSelectionLimit }))
          } else {
            // For multiple selection, add the new file to the existing selection
            const newSelection = [...localSelectedFiles, uploadedFile]
            setLocalSelectedFiles(newSelection)
            onSelect(newSelection)
          }
        } else {
          // For single selection, select only the new file
          setLocalSelectedFiles([uploadedFile])
          onSelect([uploadedFile])
        }
      }
    } catch (error: any) {
      console.error('Error uploading file:', error?.message || error)
      // Forward validation errors to the FileForm so they can be displayed next to fields
      if (error?.response?.status === 422) {
        try {
          fileFormRef.current?.handleValidationErrors?.(error)
        } catch {}
      }
      // Also show a toast for general feedback
      toast.error(t('error_uploading_file'))
      setIsUploading(false)
    }
  }

  // Use the deleteFile mutation
  const { mutate: deleteFile, isPending: isDeleting } = useDeleteFile()

  // Use the bulk delete mutation
  const { mutate: bulkDeleteFiles, isPending: isBulkDeleting } = useBulkDeleteFiles()

  // Handle delete a file
  const handleDeleteFile = (file: FileType, e: MouseEvent) => {
    e.stopPropagation()
    setFileToDelete(file)
  }

  // Confirm delete file
  const confirmDeleteFile = () => {
    if (fileToDelete) {
      deleteFile(fileToDelete.id, {
        onSuccess: async () => {
          // Remove the deleted file from local selection if it was selected
          const newSelection = localSelectedFiles.filter(f => f.id !== fileToDelete.id)
          setLocalSelectedFiles(newSelection)
          // Notify a parent component (selector.tsx) about the updated selection
          onSelect(newSelection)
          setFileToDelete(null)
          toast.success(t('deleted_successfully'))
          // Refresh the file list
          await refetch()
        },
        onError: () => {
          toast.error(t('error_deleting_file'))
        }
      })
    }
  }

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (localSelectedFiles.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  // Confirm bulk delete files
  const confirmBulkDeleteFiles = () => {
    if (localSelectedFiles.length > 0) {
      const selectedIds = localSelectedFiles.map(f => f.id)
      bulkDeleteFiles(selectedIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
            // Clear local selection
            setLocalSelectedFiles([])
            // Notify a parent component (selector.tsx) that selection is now empty
            onSelect([])
            // Refresh the file list
            await refetch()
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }
          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  // Toggle upload form visibility
  const toggleUploadForm = () => {
    setShowUploadForm(prev => !prev)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-full overflow-y-auto sm:max-w-4xl md:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{title || (onlyImages ? t('select_images') : t('select_files'))}</DialogTitle>
          <DialogDescription>
            {description || (multiple ? t('select_multiple_description') : t('select_single_description'))}
          </DialogDescription>
          {multiple && maxSelectionLimit && (
            <p className="text-muted-foreground mt-2 text-sm">
              <span className={localSelectedFiles.length >= maxSelectionLimit ? 'text-destructive font-medium' : ''}>
                {t('selection_count_limit', {
                  count: localSelectedFiles.length,
                  limit: maxSelectionLimit
                })}
              </span>
            </p>
          )}
          <div className="absolute top-6 right-10 flex items-center gap-2">
            {localSelectedFiles.length > 0 && (
              <Button variant="destructive" onClick={handleBulkDelete}>
                <Trash2Icon className="h-4 w-4" />
                <div className="hidden items-center gap-2 lg:flex">
                  <span>{t('delete_selected')}</span>
                  <span>({localSelectedFiles.length})</span>
                </div>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                toggleUploadForm()
              }}
              className="flex items-center gap-1">
              <UploadIcon className="h-4 w-4" />
              {t('upload')}
            </Button>
            <FilesFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onResetFilters={() => setFilters(initialFilters)}
              lockedType={onlyImages}
            />
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload form */}
          {showUploadForm && (
            <FileForm
              ref={fileFormRef}
              mode={FormModeEnum.CREATE}
              onSubmit={handleFileUpload}
              onCancel={() => setShowUploadForm(false)}
              isLoading={isUploading || isCreating}
              uploadProgress={mutationProgress}
              optimizeImageSelection={onlyImages && optimizeImageSelection}
              types={types}
            />
          )}

          {/* Files grid */}
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch}>
            {filesData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {filesData.items.length === 0 ? (
                    <div className="col-span-full py-8 text-center">{t('no_results')}</div>
                  ) : (
                    filesData.items.map(file => (
                      <div
                        key={file.id}
                        className={`relative cursor-pointer rounded-md border p-2 transition-all ${isFileSelected(file.id) ? 'border-primary ring-primary/20 ring-2' : 'border-border hover:border-primary/50'} `}
                        onClick={() => toggleFileSelection(file)}>
                        {/* Delete button on the left */}
                        {canDeleteFiles && (
                          <div className="absolute top-2 left-2 z-10">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="bg-destructive hover:bg-destructive/90 rounded-full p-1 opacity-60 shadow-sm transition-colors hover:opacity-100"
                                  onClick={e => handleDeleteFile(file, e)}>
                                  <Trash2Icon className="h-4 w-4 text-white" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('delete')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}

                        {/* Selection indicator on the right */}
                        <div className="absolute top-2 right-2 z-10">
                          <Checkbox
                            checked={isFileSelected(file.id)}
                            onCheckedChange={() => toggleFileSelection(file)}
                            onClick={e => e.stopPropagation()}
                            className="h-5 w-5"
                          />
                        </div>

                        {/* File preview */}
                        <div className="bg-checkerboard mb-2 flex h-32 items-center justify-center overflow-hidden rounded">
                          {file.type.startsWith('image/') ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Image
                                  src={getResizedPictureUrl(file.path, 260, 260)}
                                  alt={file.name}
                                  width={260}
                                  height={260}
                                  unoptimized
                                  className={`max-h-32 w-auto object-contain ${file.type === 'image/svg+xml' || file.path.toLowerCase().endsWith('.svg') ? 'max-w-full' : ''}`}
                                />
                              </TooltipTrigger>
                              <TooltipContent className="text-center">
                                <p>{file.name}</p>
                                <p>{file.type}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="text-muted-foreground flex flex-col items-center justify-center">
                                  <div className="w-10">
                                    <ContentFileIcon file={file} />
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="text-center">
                                <p>{file.name}</p>
                                <p>{file.type}</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>

                        {/* File info */}
                        <div className="space-y-1">
                          <p className="truncate text-sm font-medium" title={file.name}>
                            {file.name}
                          </p>
                          <p className="text-muted-foreground text-xs">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                {filesData && filesData.items.length > 0 && (
                  <DataTablePagination
                    currentPage={filesData.page}
                    totalPages={filesData.pages}
                    pageSize={filters.size || 20}
                    totalItems={filesData.total}
                    onPageChangeAction={handlePageChange}
                    onPageSizeChangeAction={handlePageSizeChange}
                  />
                )}
              </div>
            )}
          </LoadingState>
        </div>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      {fileToDelete && (
        <DeleteConfirmationDialog
          open={!!fileToDelete}
          onCancel={() => setFileToDelete(null)}
          onConfirm={confirmDeleteFile}
          isDeleting={isDeleting}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteFiles}
        isDeleting={isBulkDeleting}
        description={t('bulk_delete_confirmation_message')}
      />
    </Dialog>
  )
}

export default FileManager
